import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardLimitStore, $profileFeatureStore } from '@lib/store';
import { useCardFlow, useCurrencySymbol } from '@shared/hooks';
import { cn, FormatUtils, PaletteUtils } from '@shared/utils';
import { type CardDetailsResponse, CardKind, CardStatus, CreditCardLimit, CreditLimitsResponse, Frequency } from '@shared/api';
import { LimitRow, SidebarItem, SidebarRow } from '@widgets';
import { Button, Icon, Text } from '@shared/widgets';
import SettingIcon from '@assets/icons/setting.svg';
import SwapIcon from '@assets/icons/swap.svg';
import CancelIcon from '@assets/icons/cancel.svg';
import NotificationIcon from '@assets/icons/notification.svg';

interface CardSidebarItemsProps {
  card: CardDetailsResponse;
  openBlockModal: (arg: boolean) => void;
  openPinModal: () => void;
  openReportModal: () => void;
  openLimitsModal: () => void;
  openEditRecipientsModal: () => void;
  openDetailsModal: () => void;
  showBlockModal: boolean;
}

export const CardSidebarItems = (props: CardSidebarItemsProps) => {
  const { id } = useParams();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const cardLimitsStore = useUnit($cardLimitStore.store);

  const { t } = useTranslation('card');

  useEffect(() => {
    if (props.showBlockModal) props.openBlockModal(false);
  }, [props.card]);

  const blocked = useMemo(() => props.card.status === CardStatus.FROZEN || props.card.status === CardStatus.BLOCKED, [props.card]);
  useEffect(() => {
    props.openBlockModal(false);
  }, [blocked]);

  const cardFlow = useCardFlow(profileFeatureStore.data?.cardFlows, id ? parseInt(id) : undefined);
  const symbol = useCurrencySymbol(props.card.currency);
  const currentLimitValues = useMemo(() => {
    if (!cardLimitsStore.data) return null;
    if (cardLimitsStore.data.frequency === Frequency.DAILY) return cardLimitsStore.data.dayLimits;
    else if (cardLimitsStore.data.frequency === Frequency.CYCLE) return cardLimitsStore.data.cycleLimits;
    else if (cardLimitsStore.data.frequency === Frequency.YEAR) return cardLimitsStore.data.yearLimits;
    else return null;
  }, [cardLimitsStore.data]);
  return (
    <div
      className={cn(
        'w-80 bg-white p-6 dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && ['rounded-lg border border-gray-black', 'max-md:mt-8 max-md:w-full'],
      )}
    >
      <SidebarItem title={t('sidebar.cardDetails.title')} icon={<Icon icon={<SettingIcon />} color='inherit' />}>
        <SidebarRow
          leftRowText={t('sidebar.cardDetails.fields.cardLabel')}
          rightRowText={props.card.cardLabel || t('sidebar.cardDetails.fields.none')}
        />
        <SidebarRow
          leftRowText={t('sidebar.cardDetails.fields.cardNumber')}
          rightRowText={props.card.cardNumber && FormatUtils.toSliceNumber(props.card.cardNumber)}
        />
        <SidebarRow
          leftRowText={t('sidebar.cardDetails.fields.cardCategory')}
          rightRowText={props.card.category || t('sidebar.cardDetails.fields.none')}
        />
        {cardFlow?.editCardLabel && (
          <Button className='mb-5' fullWidth color='outlined' onClick={props.openDetailsModal}>
            {t('buttons.editDetails', { ns: 'common' })}
          </Button>
        )}
      </SidebarItem>
      <SidebarItem title={t('sidebar.cardActions.title')} icon={<Icon icon={<SwapIcon />} color='inherit' />}>
        {cardFlow?.sendPinReminder && (
          <Button className='mb-5' fullWidth color='outlined' onClick={props.openPinModal}>
            {t('sidebar.cardActions.buttons.requestPin')}
          </Button>
        )}
        {cardFlow?.block && (
          <Button className='mb-5' fullWidth color='outlined' onClick={() => props.openBlockModal(true)}>
            {t(`sidebar.cardActions.buttons.${blocked ? 'unfreezeCard' : 'freezeCard'}`)}
          </Button>
        )}
        {cardFlow?.reportCardLostStolen && (
          <Button className='mb-5' fullWidth color='outlined' onClick={props.openReportModal}>
            {t('sidebar.cardActions.buttons.report')}
          </Button>
        )}
      </SidebarItem>
      {cardLimitsStore.data && (
        <SidebarItem loading={cardLimitsStore.loading} title={t('sidebar.limits.title')} icon={<Icon icon={<CancelIcon />} color='inherit' />}>
          <div className={cn('mb-5 flex flex-col gap-5')}>
            <SidebarRow
              className='mb-0'
              leftRowText={t('sidebar.limits.fields.frequency')}
              rightRowText={t(`sidebar.limits.frequencies.${cardLimitsStore.data.frequency.toLowerCase()}`)}
            />
            <LimitRow
              label={t('sidebar.limits.fields.retailLimit')}
              currentAmount={currentLimitValues?.retailAmount || 0}
              limitAmount={cardLimitsStore.data.currentRetailAmount}
              limitNumber={cardLimitsStore.data.currentRetailNumber}
              currentNumber={currentLimitValues?.retailNumber || 0}
              symbol={symbol}
            />
            <LimitRow
              label={t('sidebar.limits.fields.cashLimit')}
              limitAmount={cardLimitsStore.data.currentCashAmount}
              currentAmount={currentLimitValues?.cashAmount || 0}
              limitNumber={cardLimitsStore.data.currentCashNumber}
              currentNumber={currentLimitValues?.cashNumber || 0}
              symbol={symbol}
            />
            {props.card.cardKind === CardKind.Credit && (
              <LimitRow
                label={t('sidebar.limits.fields.totalLimit')}
                limitAmount={(cardLimitsStore.data as CreditLimitsResponse).currentOTCAmount}
                currentAmount={(currentLimitValues as CreditCardLimit)?.otcAmount || 0}
                limitNumber={(cardLimitsStore.data as CreditLimitsResponse).currentOTCNumber}
                currentNumber={(currentLimitValues as CreditCardLimit)?.otcNumber || 0}
                symbol={symbol}
              />
            )}
            {cardFlow?.updateCardLimit && (
              <Button fullWidth color='outlined' disabled={props.card.status === CardStatus.BLOCKED} onClick={props.openLimitsModal}>
                {t('sidebar.limits.buttons.requestNewLimits')}
              </Button>
            )}
          </div>
        </SidebarItem>
      )}
      <SidebarItem
        title={t('sidebar.alertsNotifications.title')}
        icon={<Icon icon={<NotificationIcon />} color='inherit' />}
        isHidden={!cardFlow?.cardAlertsSettings.declinedAuths}
      >
        <Text className='mb-5' size='sm'>
          {t('sidebar.alertsNotifications.subtitle')}
        </Text>
        {props.card.declinedAuthorizationSubscription?.recipientDetails.map((item, idx) => (
          <SidebarRow key={idx} leftRowText={item.firstName || ''} rightRowText={item.email} />
        ))}
        <Button className='mb-5' fullWidth color='outlined' onClick={props.openEditRecipientsModal}>
          {t('sidebar.alertsNotifications.buttons.editRecipients')}
        </Button>
      </SidebarItem>
    </div>
  );
};
